/**
 * Conversion tracking events for GA4 + Meta Pixel + LinkedIn Insight Tag.
 * Safe to call even if pixels aren't loaded (checks for global functions).
 *
 * GA4 event names follow the recommended-events spec where one exists
 * (sign_up, begin_checkout, purchase); the rest are custom events.
 */

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    lintrk?: (action: string, data: Record<string, any>) => void;
    gtag?: (...args: any[]) => void;
  }
}

/** Track a Meta Pixel event */
const fbq = (...args: any[]) => {
  try { window.fbq?.(...args); } catch { /* pixel not loaded */ }
};

/** Track a LinkedIn conversion */
const lintrk = (conversionId: string) => {
  try { window.lintrk?.('track', { conversion_id: conversionId }); } catch { /* tag not loaded */ }
};

/** Track a GA4 event */
export const gtagEvent = (name: string, params?: Record<string, unknown>) => {
  try { window.gtag?.('event', name, params ?? {}); } catch { /* gtag not loaded */ }
};

// ─── Checkout handoff ──────────────────────────────────────────────
// Stripe's success redirect (/?payment_success=true) carries no value/tier,
// so begin_checkout stashes them for the purchase event to consume.

const PENDING_CHECKOUT_KEY = 'aura_pending_checkout';

export interface PendingCheckout {
  tier: string;
  value: number;
}

const stashPendingCheckout = (pending: PendingCheckout) => {
  try { sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(pending)); } catch { /* storage unavailable */ }
};

/** Read and clear the checkout stashed at begin_checkout time (null if absent/corrupt). */
export const consumePendingCheckout = (): PendingCheckout | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
    const parsed = JSON.parse(raw);
    if (typeof parsed?.tier === 'string' && typeof parsed?.value === 'number') return parsed;
    return null;
  } catch {
    return null;
  }
};

// ─── Conversion Events ─────────────────────────────────────────────

/** User creates an account (new users only — do not fire on returning logins) */
export const trackSignup = (method: string = 'email') => {
  fbq('track', 'CompleteRegistration');
  lintrk('signup');
  gtagEvent('sign_up', { method });
};

/** User starts trip generation (clicks "Design Your Itinerary") */
export const trackTripStart = (destination: string) => {
  fbq('track', 'Lead', { content_name: destination });
  fbq('trackCustom', 'TripStarted', { destination });
  lintrk('trip_started');
};

/** Premium (paid) multi-day trip synthesis completes successfully */
export const trackTripGenerated = (destination: string, days: number) => {
  fbq('trackCustom', 'TripGenerated', { destination, days });
  lintrk('trip_generated');
  gtagEvent('generate_premium', { destination, days });
};

/** Itinerary/day-plan generation failed */
export const trackGenerateFail = (flow: 'day_plan' | 'voyage' | 'atrium', errorType: string, destination?: string) => {
  const params = { flow, error_type: errorType, ...(destination ? { destination } : {}) };
  fbq('trackCustom', 'GenerateFail', params);
  gtagEvent('generate_fail', params);
};

/** User shares a trip link */
export const trackTripShared = () => {
  fbq('trackCustom', 'TripShared');
  lintrk('trip_shared');
};

/** User clicks through to Stripe checkout */
export const trackBeginCheckout = (tier: string, value: number) => {
  stashPendingCheckout({ tier, value });
  fbq('track', 'InitiateCheckout', { value, currency: 'USD', content_name: tier });
  gtagEvent('begin_checkout', {
    value,
    currency: 'USD',
    items: [{ item_id: tier, item_name: tier, price: value, quantity: 1 }],
  });
};

/** User completes a purchase (fired on the Stripe success redirect) */
export const trackPurchase = (value: number, tier: string, transactionId?: string) => {
  fbq('track', 'Purchase', { value, currency: 'USD', content_name: tier });
  lintrk('purchase');
  gtagEvent('purchase', {
    value,
    currency: 'USD',
    ...(transactionId ? { transaction_id: transactionId } : {}),
    items: [{ item_id: tier, item_name: tier, price: value, quantity: 1 }],
  });
};

/** Free single-day itinerary generated (Day Planner) */
export const trackDayPlanGenerated = (destination: string) => {
  fbq('trackCustom', 'DayPlanGenerated', { destination });
  lintrk('day_plan');
  gtagEvent('generate_itinerary', { destination });
};

/** Day Plan saved to vault */
export const trackDayPlanSaved = () => {
  fbq('trackCustom', 'DayPlanSaved');
};

/** Email captured (newsletter, sample-request form). `detail` preserves the
 *  Meta Lead content_name signal (e.g. the requested destination). */
export const trackEmailCapture = (source: 'footer' | 'landing' | 'lead_magnet' | 'sample_form', detail?: string) => {
  fbq('track', 'Lead', { content_name: detail || `email_capture_${source}` });
  gtagEvent('email_capture', { source, ...(detail ? { detail } : {}) });
};

/** B2B demo/booking CTA clicked */
export const trackDemoClick = (page: 'for-agencies' | 'for-hotels' | 'home', cta: string) => {
  fbq('trackCustom', 'DemoClick', { page, cta });
  lintrk('demo_click');
  gtagEvent('demo_click', { page, cta });
};

/** Sample itinerary viewed */
export const trackSampleView = (sample: string = 'default') => {
  fbq('trackCustom', 'SampleView', { sample });
  gtagEvent('sample_view', { sample });
};
